import { z } from 'zod';
import { ALL_ONBOARDING_FIELDS, AUDIT_ACTIONS, BACK_LINK_TYPES, BILLING_CYCLES, BROADCAST_PRESET_KEYS, BROADCAST_PRESET_META, BUILTIN_STAMP_ICONS, COMPLETION_BEHAVIORS, DEFAULT_FRONT_FIELDS, DEFAULT_EVENT_TIER_NAME, DEFAULT_MEMBER_NUMBER_LABEL, DEFAULT_STAMP_ICON, EVENT_HOLDER_NAME_MAX_LENGTH, EVENT_INCLUSION_MAX_LENGTH, EVENT_MAX_BATCH_ISSUE, EVENT_MAX_INCLUSIONS, EVENT_MAX_TIERS, EVENT_NAME_MAX_LENGTH, EVENT_SEAT_FIELD_MAX_LENGTH, EVENT_TIER_NAME_MAX_LENGTH, EVENT_VENUE_ADDRESS_MAX_LENGTH, EVENT_VENUE_NAME_MAX_LENGTH, MEMBERSHIP_BENEFIT_MAX_LENGTH, MEMBERSHIP_MAX_BENEFITS, MEMBERSHIP_MAX_TIERS, MEMBERSHIP_NUMBER_LABEL_MAX_LENGTH, MEMBERSHIP_TIER_MAX_LENGTH, INVOICE_STATUSES, LOYALTY_DEFAULT_STAMPS, LOYALTY_MAX_STAMPS, LOYALTY_MIN_STAMPS, MAX_BACK_LINKS, MAX_FRONT_FIELDS, MAX_SEGMENT_RULES, NAYA_TERRACOTTA, OWNER_TERMS_MAX_LENGTH, PAYMENT_METHODS, PLANS, PROGRAM_STATUSES, ROLES, TENANT_STATUSES, WALLET_PROVIDERS, } from '../constants/index.js';
import { isReservedSlug, validateSlugFormat } from '../utils/slug.js';
// ----- Loyalty program -----
export const MilestoneSchema = z.object({
    at_stamp: z.number().int().min(1).max(LOYALTY_MAX_STAMPS),
    reward_text: z.string().min(1).max(120),
    type: z.enum(['intermediate', 'final']),
});
export const LoyaltyProgramSchema = z
    .object({
    name: z.string().min(1).max(60),
    total_stamps: z.number().int().min(LOYALTY_MIN_STAMPS).max(LOYALTY_MAX_STAMPS),
    milestones: z.array(MilestoneSchema).min(1),
})
    .refine((p) => p.milestones.every((m) => m.at_stamp <= p.total_stamps), 'Milestone position cannot exceed total stamps')
    .refine((p) => p.milestones.some((m) => m.type === 'final'), 'Program must have at least one final milestone');
// Snapshot of the program embedded into each customer card.
export const ProgramSnapshotSchema = z.object({
    name: z.string().min(1).max(60),
    total_stamps: z.number().int().min(LOYALTY_MIN_STAMPS).max(LOYALTY_MAX_STAMPS),
    milestones: z.array(MilestoneSchema).min(1),
});
// ----- Pass-type program caps (SLICE-PASS-TYPES) -----
//
// Per-pass-type cap on how many programs a tenant may create. One value per
// pass type: null = unlimited, 0 = type disabled (not allowed), N > 0 = at most
// N programs of that type. A type's "allowed?" is derived (passTypeAllowed).
// Keep the keys in sync with PASS_TYPES.
const programCapValue = z.number().int().min(0).max(1000).nullable();
export const ProgramCapsSchema = z.object({
    loyalty: programCapValue,
    membership: programCapValue,
    event_ticket: programCapValue,
});
/** A pass type is allowed when its cap is unlimited (null) or a positive
 *  number. 0 / undefined = not allowed. */
export function passTypeAllowed(cap) {
    return cap === null || (typeof cap === 'number' && cap > 0);
}
// ----- Pass builder (slice 4a-3) -----
//
// The 4a-3 owner builder produces a richer program than the thin 2a model
// above. It reuses MilestoneSchema unchanged — the "grand prize" is the single
// milestone with `type: 'final'` (the builder pins it at `at_stamp ===
// total_stamps`). Server and client both validate against these schemas.
const HexColor = z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a #RRGGBB hex color');
// A custom-uploaded stamp icon URL (https in prod, /dev-uploads in local dev)
// OR a built-in reference of the form `builtin:<id>:<filled|empty>`.
const BUILTIN_ICON_REF = new RegExp(`^builtin:(${BUILTIN_STAMP_ICONS.join('|')}):(filled|empty)$`);
const StampIconRef = z
    .string()
    .min(1)
    .refine((v) => BUILTIN_ICON_REF.test(v) ||
    v.startsWith('https://') ||
    v.startsWith('/dev-uploads/'), 'Must be a built-in icon reference or an uploaded icon URL');
// An owner/admin-supplied image URL the SERVER will fetch (hero photos). Must be
// https (prod, R2/GCS) or an origin-relative /dev-uploads path (local backend).
// Plain `http://` is rejected so it can't be pointed at an internal/metadata
// host — the SSRF host allowlist in services/wallet/image-host.ts is the
// runtime backstop, this keeps the bad value out of the DB in the first place.
const ServerFetchableImageUrl = z
    .string()
    .refine((v) => v.startsWith('https://') || v.startsWith('/dev-uploads/'), 'Must be an https URL or a /dev-uploads path');
// Onboarding fields. first_name + email are immutable {enabled:true,
// required:true} — the schema literally forces them so a malformed payload
// can't disable them. The four optional fields default OFF.
const ImmutableOnboardingField = z.object({
    enabled: z.literal(true),
    required: z.literal(true),
});
const OptionalOnboardingFieldSchema = z.object({
    enabled: z.boolean(),
    required: z.boolean(),
});
export const OnboardingFieldsSchema = z
    .object({
    first_name: ImmutableOnboardingField,
    email: ImmutableOnboardingField,
    last_name: OptionalOnboardingFieldSchema,
    // `birthday` captures month + day together (MM-DD) — one field on the
    // sign-up form rather than two separate ones.
    birthday: OptionalOnboardingFieldSchema,
    phone: OptionalOnboardingFieldSchema,
})
    .refine(
// A field can only be "required" if it's also "enabled".
(f) => [f.last_name, f.birthday, f.phone].every((x) => !x.required || x.enabled), 'An optional field cannot be required unless it is enabled');
export const PassDesignSchema = z.object({
    primary_color: HexColor,
    text_color: HexColor,
    stamp_filled_url: StampIconRef,
    stamp_empty_url: StampIconRef,
    // Which collected customer fields show in the pass-front detail row. The
    // owner picks from the *enabled* onboarding fields (the builder enforces
    // that); the schema only caps the count and dedupes. The pass-front render
    // and the activation check both tolerate an empty array — a pass with no
    // detail row is valid. Pre-front_fields rows are backfilled by migration
    // 0009 (pass_design is jsonb, so it's a data UPDATE, not DDL).
    front_fields: z
        .array(z.enum(ALL_ONBOARDING_FIELDS))
        .max(MAX_FRONT_FIELDS)
        .refine((f) => new Set(f).size === f.length, 'front_fields must be unique'),
    // Label rendered next to the stamp count on the Google Wallet pass
    // (loyaltyPoints.label) and in our preview. Default "Stamps" — owner can
    // localize or rebrand ("Visits", "Sips", "Sello", etc.). Cosmetic, patchable
    // post-activation; flows live to issued passes via class patch.
    points_label: z.string().min(1).max(20),
    // Optional descriptive blurb rendered as a class-level textModule below the
    // points block on Google Wallet ("How it works", program tagline, etc.).
    // Cosmetic, patchable post-activation. Empty string = no module emitted.
    pass_description: z.string().max(200),
    // Slice 4b — label rendered above the customer's name on the wallet pass via
    // Google Wallet's `localizedAccountNameLabel`. Default "Member" gives the
    // header a membership-card feel ("Member · Juan") instead of the generic
    // bare-name default. Owner can rebrand ("Friend", "Sili Squad", etc.).
    // Cosmetic, patchable post-activation, propagates via syncTenantProgramCosmetic.
    // JSONB-only addition — backfilled in seed.ts with the same self-heal pattern
    // 4a-6 used for points_label / pass_description.
    account_name_label: z.string().min(1).max(20),
    // Color used to tint built-in stamp icons (and the milestone overlays on
    // the hero PNG). Defaults to the same value as text_color via the seed
    // backfill so existing programs render identically; owners can pick
    // something different in the builder to make stamps stand out from text.
    stamp_color: HexColor,
    // Optional owner-uploaded hero photo. Three coordinated fields:
    //   hero_original_url    — the cropped 1032x336 PNG (no brightness applied).
    //                          Source asset re-fed into the brightness slider on
    //                          re-edit. R2-served (browser-facing).
    //   hero_brightness      — number in [-70, 0], default 0 (no change). Applied
    //                          server-side via sharp.modulate during composition.
    //                          Caps at -70 because anything darker washes out
    //                          contrast with white stamps without helping.
    //   hero_wallet_image_url — the FINAL composite Google Wallet sees: photo
    //                          darkened by hero_brightness + stamps + milestone
    //                          decorations baked on top. GCS-served (Google's
    //                          image fetcher only trusts Google-owned hosts —
    //                          see [[feedback-google-wallet-host-trust]]).
    // All three null = no hero photo, fall back to the solid-color stamp grid.
    hero_original_url: ServerFetchableImageUrl.nullable().default(null),
    hero_brightness: z.number().int().min(-70).max(0).default(0),
    hero_wallet_image_url: ServerFetchableImageUrl.nullable().default(null),
});
export const BackLinkSchema = z.object({
    type: z.enum(BACK_LINK_TYPES),
    value: z.string().min(1).max(500),
    display_text: z.string().min(1).max(60),
});
export const BackLinksSchema = z.array(BackLinkSchema).max(MAX_BACK_LINKS);
// ----- Referral config (SLICE-REFERRAL) -----
//
// Per-program owner referral settings. Stored as live cosmetic-style JSONB on
// loyalty_programs.referral_config — read at enroll + stamp time, NEVER copied
// into program_snapshot. Per-referral terms are frozen on the referrals row at
// referral time, so a later edit here never re-prices a pending referral. The
// two upstream switches (tenants.referral_entitled + REFERRAL_ENABLED env)
// gate whether this is even reachable; all three must be on for anything to
// render / enroll / credit.
export const REFERRAL_MIN_STAMPS = 1;
export const REFERRAL_MAX_STAMPS = LOYALTY_MAX_STAMPS; // 15
export const ReferralConfigSchema = z.object({
    enabled: z.boolean(),
    // Flat bonus stamps the referrer earns per successful referral.
    referrer_bonus_stamps: z
        .number()
        .int()
        .min(REFERRAL_MIN_STAMPS)
        .max(REFERRAL_MAX_STAMPS),
    // First-visit multiplier applied to the referee's very first stamp (2 = the
    // first stamp counts double). Welcome only — capped low on purpose.
    referee_welcome_multiplier: z.number().int().min(2).max(3),
    // Stamps the referee must earn before the referrer is credited. This is both
    // the engagement lever and the fraud wall (a stamp needs a real purchase).
    referee_threshold_stamps: z
        .number()
        .int()
        .min(REFERRAL_MIN_STAMPS)
        .max(REFERRAL_MAX_STAMPS),
    // Lifetime cap on credited referrals per (referrer customer, program).
    // Bounds liability given there is no referral expiry.
    max_referrals_per_person: z.number().int().min(1).max(1000),
});
// Sensible starting point when an owner first turns referral on (builder +
// Referrals page seed from this). All within ReferralConfigSchema bounds and
// safe against the 5-stamp minimum program.
export const DEFAULT_REFERRAL_CONFIG = {
    enabled: false,
    referrer_bonus_stamps: 2,
    referee_welcome_multiplier: 2,
    referee_threshold_stamps: 3,
    max_referrals_per_person: 5,
};
// Cross-field validity for a referral config against the program's stamp
// count. Returns issue strings (empty = valid). Mirrors the broadcast-validity
// pattern: the schema bounds each field, the service checks the cross-field
// rules against the persisted total_stamps and throws a readable error.
export function referralConfigIssues(c, totalStamps) {
    const issues = [];
    if (c.referee_threshold_stamps > totalStamps) {
        issues.push(`referral: the friend's stamp goal (${c.referee_threshold_stamps}) can't exceed the card size (${totalStamps})`);
    }
    if (c.referrer_bonus_stamps > totalStamps) {
        issues.push(`referral: the referrer bonus (${c.referrer_bonus_stamps}) can't exceed the card size (${totalStamps})`);
    }
    return issues;
}
// ----- Secret-menu config (off-menu stamp tracking, 2026-09-02) -----
//
// Per-program owner config for the secret/off-menu-menu stamp mechanic.
// Stored as jsonb on loyalty_programs.secret_menu_config. Unlike
// ReferralConfigSchema above, `required_secret_stamps` and `reward_text`
// LOCK the first time they're saved with real values (`locked: true`) — the
// API's patchProgram rejects further edits to those two fields once locked,
// so a customer's eligibility, once earned, can never be invalidated by the
// owner moving the goalposts later. `enabled` stays freely toggleable
// forever — see services/secret-menu/config.ts for why toggling it off does
// NOT retroactively affect redemption eligibility for stamps already banked.
export const SECRET_MENU_MIN_STAMPS = 1;
export const SECRET_MENU_MAX_STAMPS = LOYALTY_MAX_STAMPS;
export const SecretMenuConfigSchema = z.object({
    enabled: z.boolean(),
    required_secret_stamps: z
        .number()
        .int()
        .min(SECRET_MENU_MIN_STAMPS)
        .max(SECRET_MENU_MAX_STAMPS),
    reward_text: z.string().min(1).max(200),
    // Flips true on the first save where required_secret_stamps/reward_text
    // are real (non-default) values. Once true, the API rejects any further
    // change to those two fields — only `enabled` may still change.
    locked: z.boolean().default(false),
});
// Sensible starting point when an owner first turns secret-menu on. `locked`
// stays false until the owner's first real save.
export const DEFAULT_SECRET_MENU_CONFIG = {
    enabled: false,
    required_secret_stamps: 5,
    reward_text: '',
    locked: false,
};
// Cross-field validity against the program's stamp count. Mirrors
// referralConfigIssues.
export function secretMenuConfigIssues(c, totalStamps) {
    const issues = [];
    if (c.required_secret_stamps > totalStamps) {
        issues.push(`secret menu: the off-menu stamp goal (${c.required_secret_stamps}) can't exceed the card size (${totalStamps})`);
    }
    return issues;
}
export const ProgramStatusSchema = z.enum(PROGRAM_STATUSES);
export const CompletionBehaviorSchema = z.enum(COMPLETION_BEHAVIORS);
// ----- Pass expiration (smoke-test follow-up, 2026-05-21) -----
// Owner-configurable card expiration. Only offered for 'stay_completed'
// programs (a 'reset' card recycles forever — see the API-side refinement).
// The four fields mirror the loyalty_programs columns; the shape rules
// (which fields are set per mode) are enforced by `expirationShapeIssues`.
export const ExpirationModeSchema = z.enum(['none', 'relative', 'fixed']);
export const ExpirationRelativeUnitSchema = z.enum(['days', 'months', 'years']);
// Flat expiration config — appears on the program row, the patch schema, and
// the activate schema. `_fixed_date` is a YYYY-MM-DD string (matches the
// Postgres `date` column and Google Wallet's date inputs).
export const ExpirationConfigSchema = z.object({
    expiration_mode: ExpirationModeSchema,
    expiration_relative_unit: ExpirationRelativeUnitSchema.nullable(),
    expiration_relative_amount: z.number().int().positive().nullable(),
    expiration_fixed_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a YYYY-MM-DD date')
        .nullable(),
});
// Cross-field validity for an expiration config. Returns issue strings
// (empty = valid). `relative` needs unit+amount; `fixed` needs the date;
// `none` needs everything null. Mirrors the DB CHECK constraint so a bad
// config is rejected at the API edge with a readable message.
export function expirationShapeIssues(c) {
    const issues = [];
    if (c.expiration_mode === 'none') {
        if (c.expiration_relative_unit !== null ||
            c.expiration_relative_amount !== null ||
            c.expiration_fixed_date !== null) {
            issues.push('expiration: “never expires” must not carry any expiry detail');
        }
    }
    else if (c.expiration_mode === 'relative') {
        if (c.expiration_relative_unit === null || c.expiration_relative_amount === null) {
            issues.push('expiration: a relative expiry needs both a unit and an amount');
        }
        if (c.expiration_fixed_date !== null) {
            issues.push('expiration: a relative expiry must not also carry a fixed date');
        }
    }
    else {
        // fixed
        if (c.expiration_fixed_date === null) {
            issues.push('expiration: a fixed expiry needs a date');
        }
        if (c.expiration_relative_unit !== null || c.expiration_relative_amount !== null) {
            issues.push('expiration: a fixed expiry must not also carry a relative period');
        }
    }
    return issues;
}
/**
 * Resolve a concrete per-card expiry from a program's expiration config and
 * the card's issue date. Returns `null` when the program never expires.
 * Pure — used by API enrollment to stamp `customer_cards.expires_at`.
 *   - 'relative' → issuedAt + N units (calendar math for months/years).
 *   - 'fixed'    → end-of-day on the program's shared date (so the card is
 *                  valid through the whole final day).
 */
export function resolveCardExpiry(c, issuedAt) {
    if (c.expiration_mode === 'none')
        return null;
    if (c.expiration_mode === 'fixed') {
        if (!c.expiration_fixed_date)
            return null;
        // End-of-day UTC on the fixed date.
        return new Date(`${c.expiration_fixed_date}T23:59:59.999Z`);
    }
    // relative
    if (c.expiration_relative_unit === null || c.expiration_relative_amount === null) {
        return null;
    }
    const d = new Date(issuedAt.getTime());
    const n = c.expiration_relative_amount;
    switch (c.expiration_relative_unit) {
        case 'days':
            d.setUTCDate(d.getUTCDate() + n);
            break;
        case 'months':
            d.setUTCMonth(d.getUTCMonth() + n);
            break;
        case 'years':
            d.setUTCFullYear(d.getUTCFullYear() + n);
            break;
    }
    return d;
}
// ----- Membership pass (SLICE-MEMBERSHIP-PASS) -----
//
// Program-level membership config, stored in loyalty_programs.type_config
// (jsonb, 0028). Expiration deliberately does NOT live here — memberships
// reuse the SAME expiration_* program columns + resolveCardExpiry as loyalty
// (gym terms: relative 1 month / 1 year). Tier + benefits + label are FROZEN
// per card at issuance (MembershipSnapshotSchema below); self_enroll_enabled
// is read LIVE at enroll time (it's a gate, not card content).
/** Drop blank/whitespace-only entries from a string array (preprocess). The
 *  builder seeds an empty editing row when the owner clicks "+ Add tier/benefit";
 *  an in-progress autosave would otherwise 400 on the empty string. Non-arrays
 *  pass through untouched so the inner schema still reports a type error. */
function dropBlankStrings(v) {
    if (!Array.isArray(v))
        return v;
    return v
        .map((s) => (typeof s === 'string' ? s.trim() : s))
        .filter((s) => s !== '');
}
export const MembershipConfigSchema = z.object({
    // Tier switch — OFF by default. When off, the pass shows no tier line (a
    // single-level membership); the `tiers` list is ignored. When on, the owner
    // defines `tiers` (the selectable options) and one is chosen per member at
    // enrollment (staff picks for a gated membership; the member picks when
    // self-enroll is on). The chosen tier is frozen on the card snapshot and
    // overridable later per-member (customer_cards.type_state.tier_name).
    tiers_enabled: z.boolean().default(false),
    // Owner-customizable tier options ("Gold", "Silver", "Bronze", "VIP" …).
    // Trimmed, non-empty, de-dup enforced by `membershipConfigIssues`. Only
    // meaningful when `tiers_enabled` is true. Blank editing rows are dropped
    // (preprocess) so an in-progress builder autosave doesn't 400 on an empty
    // input the owner hasn't filled yet.
    tiers: z
        .preprocess(dropBlankStrings, z
        .array(z.string().trim().min(1).max(MEMBERSHIP_TIER_MAX_LENGTH))
        .max(MEMBERSHIP_MAX_TIERS))
        .default([]),
    // Back-of-pass perks list. One line each. Shared across all tiers (V1).
    // Blank editing rows dropped (same reason as tiers).
    benefits: z
        .preprocess(dropBlankStrings, z
        .array(z.string().trim().min(1).max(MEMBERSHIP_BENEFIT_MAX_LENGTH))
        .max(MEMBERSHIP_MAX_BENEFITS))
        .default([]),
    member_number_label: z
        .string()
        .trim()
        .min(1)
        .max(MEMBERSHIP_NUMBER_LABEL_MAX_LENGTH)
        .default(DEFAULT_MEMBER_NUMBER_LABEL),
    // Memberships are typically PAID → owner-gated issuance by default. When
    // false, the public enroll page requires a valid staff-minted enrollment
    // token; the cold-enroll program auto-select skips this program entirely.
    self_enroll_enabled: z.boolean().default(false),
});
/**
 * Cross-field validation for a membership config. Surfaced as activation gaps
 * and enforced on PATCH. When tiers are enabled there must be at least one tier
 * and the names must be unique (case-insensitive) — an empty/duplicated tier
 * list would make the enrollment picker meaningless.
 */
export function membershipConfigIssues(c) {
    const issues = [];
    if (c.tiers_enabled) {
        if (c.tiers.length === 0) {
            issues.push('tiers: add at least one tier, or turn tiers off');
        }
        const seen = new Set();
        for (const t of c.tiers) {
            const key = t.trim().toLowerCase();
            if (seen.has(key)) {
                issues.push(`tiers: "${t}" is listed more than once`);
                break;
            }
            seen.add(key);
        }
    }
    return issues;
}
/**
 * Resolve the tier a card should carry at enrollment, validated against the
 * program config. Returns the canonical tier string (matched case-insensitively
 * to the configured options) or null when tiers are off. Throws nothing — the
 * caller decides how to treat an invalid/missing selection.
 */
export function resolveEnrollmentTier(c, requested) {
    if (!c.tiers_enabled || c.tiers.length === 0)
        return { ok: true, tier: null };
    const want = (requested ?? '').trim().toLowerCase();
    const match = c.tiers.find((t) => t.trim().toLowerCase() === want);
    if (!match)
        return { ok: false, tier: null };
    return { ok: true, tier: match };
}
// What a membership card freezes at issuance (customer_cards.program_snapshot
// for pass_type='membership' — the loyalty ProgramSnapshotSchema does not
// apply). Per the snapshot invariant, later program edits only affect new
// members.
export const MembershipSnapshotSchema = z.object({
    name: z.string().min(1).max(60),
    tier_name: z.string().nullable(),
    benefits: z.array(z.string()),
    member_number_label: z.string(),
});
// Per-card mutable membership state (customer_cards.type_state). tier_name
// overrides the snapshot tier when set — written by the owner tier-change
// PATCH, synced to both wallets.
export const MembershipStateSchema = z.object({
    tier_name: z
        .string()
        .trim()
        .min(1)
        .max(MEMBERSHIP_TIER_MAX_LENGTH)
        .nullable()
        .default(null),
});
// Owner tier-change request body (PATCH /api/owner/cards/:id/membership).
export const MembershipTierChangeSchema = z.object({
    tier_name: z
        .string()
        .trim()
        .min(1)
        .max(MEMBERSHIP_TIER_MAX_LENGTH)
        .nullable(),
});
// Strict whole-program schema for membership activation — the membership
// analogue of ProgramActivateSchema (no stamps, no milestones, no
// completion_behavior).
export const MembershipActivateSchema = z.object({
    name: z.string().min(1).max(60),
    onboarding_fields: OnboardingFieldsSchema,
    pass_design: PassDesignSchema,
    back_links: BackLinksSchema,
    owner_terms: z.string().max(OWNER_TERMS_MAX_LENGTH),
    expiration_mode: ExpirationModeSchema,
    expiration_relative_unit: ExpirationRelativeUnitSchema.nullable(),
    expiration_relative_amount: z.number().int().positive().nullable(),
    expiration_fixed_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a YYYY-MM-DD date')
        .nullable(),
    type_config: MembershipConfigSchema,
});
// Cross-field activation gaps for a membership program. Unlike loyalty there
// is no completion_behavior, so expiry is allowed in any mode. Tier config is
// validated here too (tiers on → at least one, unique names).
export function collectMembershipActivationGaps(p) {
    return [...expirationShapeIssues(p), ...membershipConfigIssues(p.type_config)];
}
// Builder defaults for a fresh membership draft. Stamp icon refs are unused
// by the membership renderers but PassDesignSchema requires them — keep the
// loyalty defaults so the one pass_design shape serves both types.
export function buildDefaultMembershipDraft() {
    const loyalty = buildDefaultProgramDraft();
    return {
        name: 'Membership',
        onboarding_fields: loyalty.onboarding_fields,
        pass_design: {
            ...loyalty.pass_design,
            // No stamp grid on a membership — the detail row carries the content.
            points_label: 'Tier',
            account_name_label: 'Member',
        },
        back_links: [],
        owner_terms: '',
        expiration_mode: 'none',
        expiration_relative_unit: null,
        expiration_relative_amount: null,
        expiration_fixed_date: null,
        type_config: {
            tiers_enabled: false,
            tiers: [],
            benefits: [],
            member_number_label: DEFAULT_MEMBER_NUMBER_LABEL,
            self_enroll_enabled: false,
        },
    };
}
/**
 * Extend a membership card's expiry by ONE program period ('relative' mode
 * only). Early renewal stacks (extends from the current expiry); lapsed
 * renewal restarts from `now`. Pure — the renew route persists the result and
 * syncs wallets. Returns null when the config isn't renewable ('none' never
 * expires; 'fixed' season passes just end).
 */
export function resolveRenewedExpiry(c, currentExpiresAt, now) {
    if (c.expiration_mode !== 'relative')
        return null;
    const base = currentExpiresAt && currentExpiresAt.getTime() > now.getTime()
        ? currentExpiresAt
        : now;
    return resolveCardExpiry(c, base);
}
// ----- Event ticket pass (SLICE-EVENT-TICKET-PASS) -----
//
// Program-level event config, stored in loyalty_programs.type_config (jsonb,
// 0028). A one-time admission pass: event name, date, venue, and a list of
// TIERS (GA / VIP / Early Bird) each with an optional price + perks. Event
// details + the chosen tier are FROZEN per ticket at issuance (event details in
// program_snapshot via EventTicketSnapshotSchema, the tier + seat + holder in
// customer_cards.type_state via EventTicketStateSchema) — later program edits
// only affect new tickets, same snapshot invariant as loyalty/membership.
//
// `event_name` is a REQUIRED KEY (empty string allowed mid-draft) — it is the
// disambiguator that lets the generic `type_config` union tell an event config
// apart from a membership config (which never carries the key). Event programs
// do NOT use the program-level expiration_* columns; the ticket's validity is
// its event date, resolved onto customer_cards.expires_at by the handler.
/** Drop tier rows whose name is blank (the builder seeds an empty editing row
 *  when the owner clicks "+ Add tier"; an in-progress autosave would otherwise
 *  400). Mirrors `dropBlankStrings` for the object-shaped tier list. */
function dropBlankTiers(v) {
    if (!Array.isArray(v))
        return v;
    return v.filter((t) => t && typeof t === 'object' && String(t.name ?? '').trim() !== '');
}
/** Coerce a blank/whitespace string to undefined (per optional seat field). */
function blankToUndef(v) {
    if (typeof v === 'string' && v.trim() === '')
        return undefined;
    return v;
}
// One ticket tier. `id` is a stable opaque handle (the builder assigns it once
// and never rewrites it on rename) so an issued ticket's `type_state.tier_id`
// keeps resolving. `price_centavos` is informational in V1 (no gateway) and is
// what a future paid checkout charges. `inclusions` render on the pass back.
export const EventTierSchema = z.object({
    id: z.string().trim().min(1).max(40),
    name: z.string().trim().min(1).max(EVENT_TIER_NAME_MAX_LENGTH),
    price_centavos: z.number().int().nonnegative().nullable().default(null),
    inclusions: z
        .preprocess(dropBlankStrings, z
        .array(z.string().trim().min(1).max(EVENT_INCLUSION_MAX_LENGTH))
        .max(EVENT_MAX_INCLUSIONS))
        .default([]),
    // Optional per-tier capacity. Independent of the event-wide cap — issuance
    // must satisfy BOTH. null = unlimited (subject to the event cap).
    capacity: z.number().int().positive().nullable().default(null),
});
// Per-ticket free-text seat. No reserved-seating engine in V1 — the issuer
// types whatever the venue uses; uniqueness is not enforced. All optional.
export const EventSeatSchema = z.object({
    section: z.preprocess(blankToUndef, z.string().trim().max(EVENT_SEAT_FIELD_MAX_LENGTH).optional()),
    row: z.preprocess(blankToUndef, z.string().trim().max(EVENT_SEAT_FIELD_MAX_LENGTH).optional()),
    seat: z.preprocess(blankToUndef, z.string().trim().max(EVENT_SEAT_FIELD_MAX_LENGTH).optional()),
    gate: z.preprocess(blankToUndef, z.string().trim().max(EVENT_SEAT_FIELD_MAX_LENGTH).optional()),
});
export const EventTicketConfigSchema = z.object({
    // REQUIRED KEY (union disambiguator). Empty string tolerated mid-draft;
    // eventTicketConfigIssues enforces non-empty at activation.
    event_name: z.string().max(EVENT_NAME_MAX_LENGTH),
    // ISO datetime at the edge (timestamptz). Loose here for draft tolerance;
    // validated at activation. ends_at optional (single-instant events).
    starts_at: z.string(),
    ends_at: z.preprocess(blankToUndef, z.string().optional()),
    venue_name: z.string().max(EVENT_VENUE_NAME_MAX_LENGTH),
    venue_address: z.preprocess(blankToUndef, z.string().max(EVENT_VENUE_ADDRESS_MAX_LENGTH).optional()),
    // Geo for Apple lock-screen relevance + Google location. Both or neither.
    venue_lat: z.number().min(-90).max(90).nullable().default(null),
    venue_lng: z.number().min(-180).max(180).nullable().default(null),
    // Collect attendee name at issue/claim (printed on the ticket).
    holder_name_required: z.boolean().default(false),
    // Optional TOTAL cap across all tiers. null = unlimited.
    capacity: z.number().int().positive().nullable().default(null),
    // 1..N tiers. A fresh draft seeds a single "General Admission" tier
    // (buildDefaultEventTicketDraft). Blank-name rows dropped for draft autosave;
    // eventTicketConfigIssues enforces >=1 tier + unique ids/names at activation.
    tiers: z
        .preprocess(dropBlankTiers, z.array(EventTierSchema).max(EVENT_MAX_TIERS))
        .default([]),
});
/** Loose ISO-datetime check (Date.parse round-trips the value). Pure, no
 *  Date.now — used only to validate a provided string, not to read the clock. */
function isParseableDate(s) {
    return s.trim() !== '' && !Number.isNaN(Date.parse(s));
}
/**
 * Cross-field validation for an event config. Surfaced as activation gaps and
 * (defense-in-depth) enforceable on issuance. Empty list = ready.
 */
export function eventTicketConfigIssues(c) {
    const issues = [];
    if (c.event_name.trim().length === 0) {
        issues.push('event: the event needs a name');
    }
    if (!isParseableDate(c.starts_at)) {
        issues.push('event: a valid start date and time is required');
    }
    if (c.ends_at != null && c.ends_at.trim() !== '') {
        if (!isParseableDate(c.ends_at)) {
            issues.push('event: the end time is not a valid date');
        }
        else if (isParseableDate(c.starts_at) && Date.parse(c.ends_at) < Date.parse(c.starts_at)) {
            issues.push('event: the end time cannot be before the start time');
        }
    }
    if (c.venue_name.trim().length === 0) {
        issues.push('event: the venue needs a name');
    }
    // Geo must be supplied as a pair (both for a usable location pin).
    if ((c.venue_lat == null) !== (c.venue_lng == null)) {
        issues.push('event: latitude and longitude must be set together');
    }
    if (c.tiers.length === 0) {
        issues.push('tiers: add at least one ticket tier');
    }
    const seenId = new Set();
    const seenName = new Set();
    for (const t of c.tiers) {
        if (seenId.has(t.id)) {
            issues.push(`tiers: duplicate tier id "${t.id}"`);
        }
        seenId.add(t.id);
        const nameKey = t.name.trim().toLowerCase();
        if (seenName.has(nameKey)) {
            issues.push(`tiers: "${t.name}" is listed more than once`);
        }
        seenName.add(nameKey);
        // A per-tier cap larger than the event cap is a config mistake.
        if (c.capacity != null && t.capacity != null && t.capacity > c.capacity) {
            issues.push(`tiers: "${t.name}" capacity exceeds the event capacity`);
        }
    }
    return issues;
}
/**
 * Resolve which tier a ticket should carry at issuance/claim, validated against
 * the LIVE program config. When no tier is requested and the event has exactly
 * one tier, that tier is implied. Returns the matched tier or null + ok=false
 * for an invalid/ambiguous selection. Pure — caller persists the snapshot.
 */
export function resolveEventTierSelection(c, requestedTierId) {
    if (requestedTierId == null || requestedTierId === '') {
        const sole = c.tiers.length === 1 ? c.tiers[0] : null;
        return sole ? { ok: true, tier: sole } : { ok: false, tier: null };
    }
    const match = c.tiers.find((t) => t.id === requestedTierId);
    return match ? { ok: true, tier: match } : { ok: false, tier: null };
}
/**
 * Remaining seats for a cap given the current occupied count. The occupied
 * count (status IN active/used/pending_payment) is queried in the API; this is
 * the pure arithmetic. null cap = unlimited (returns null).
 */
export function remainingSeats(cap, occupied) {
    if (cap == null)
        return null;
    return Math.max(0, cap - occupied);
}
// What an event ticket freezes at issuance into customer_cards.program_snapshot
// (the loyalty ProgramSnapshotSchema does not apply). Event-wide details only —
// the chosen tier + seat + holder live per-card in type_state.
export const EventTicketSnapshotSchema = z.object({
    name: z.string().min(1).max(60),
    event_name: z.string(),
    starts_at: z.string(),
    ends_at: z.string().nullable(),
    venue_name: z.string(),
    venue_address: z.string().nullable(),
    venue_lat: z.number().nullable(),
    venue_lng: z.number().nullable(),
    holder_name_required: z.boolean(),
});
// Per-card event-ticket state (customer_cards.type_state). The tier snapshot is
// frozen at issue. Check-in metadata (used_*) is written atomically by the gate
// scan. `payment` is the seam for a future gateway — 'free' in V1.
export const EventPaymentStateSchema = z.object({
    status: z.enum(['free', 'pending', 'paid']).default('free'),
});
export const EventTicketStateSchema = z.object({
    tier_id: z.string(),
    tier_name: z.string(),
    tier_price_centavos: z.number().int().nonnegative().nullable().default(null),
    tier_inclusions: z.array(z.string()).default([]),
    seat: EventSeatSchema.nullable().default(null),
    holder_name: z.string().max(EVENT_HOLDER_NAME_MAX_LENGTH).nullable().default(null),
    used_at: z.string().nullable().default(null),
    used_by_staff_id: z.string().nullable().default(null),
    used_branch_id: z.string().nullable().default(null),
    payment: EventPaymentStateSchema.nullable().default(null),
});
// Strict whole-program schema for event-ticket activation — the event analogue
// of MembershipActivateSchema (no stamps/milestones/completion_behavior; event
// programs don't use the program expiration columns, so they're omitted).
export const EventTicketActivateSchema = z.object({
    name: z.string().min(1).max(60),
    onboarding_fields: OnboardingFieldsSchema,
    pass_design: PassDesignSchema,
    back_links: BackLinksSchema,
    owner_terms: z.string().max(OWNER_TERMS_MAX_LENGTH),
    type_config: EventTicketConfigSchema,
});
// Cross-field activation gaps for an event program (just the config issues —
// there are no milestone/expiry rules to layer on).
export function collectEventTicketActivationGaps(p) {
    return eventTicketConfigIssues(p.type_config);
}
// ----- Event ticket request bodies -----
// Owner issues N tickets of one tier. holder_name + seat apply to every ticket
// in the batch (vary seats by issuing separate batches). quantity capped at
// EVENT_MAX_BATCH_ISSUE — concert-scale needs an async worker (future slice).
export const EventTicketIssueRequestSchema = z.object({
    tier_id: z.preprocess(blankToUndef, z.string().trim().min(1).optional()),
    quantity: z.number().int().min(1).max(EVENT_MAX_BATCH_ISSUE).default(1),
    holder_name: z.preprocess(blankToUndef, z.string().trim().max(EVENT_HOLDER_NAME_MAX_LENGTH).optional()),
    seat: EventSeatSchema.optional(),
});
// Public self-claim — the attendee picks a tier (when the event has >1),
// optionally a holder name, and MUST consent (PH DPA). first_name is collected
// for the customer row.
export const EventClaimRequestSchema = z.object({
    tier_id: z.preprocess(blankToUndef, z.string().trim().min(1).optional()),
    holder_name: z.preprocess(blankToUndef, z.string().trim().max(EVENT_HOLDER_NAME_MAX_LENGTH).optional()),
    first_name: z.preprocess(blankToUndef, z.string().trim().max(60).optional()),
    consented_at: z.string().min(1),
});
// Gate check-in — the scanned stamp token (same HMAC envelope as a loyalty stamp).
export const TicketCheckInRequestSchema = z.object({
    stamp_token: z.string().min(1),
});
// Builder defaults for a fresh event draft. Seeds a single default tier so
// every ticket has one even before the owner customizes the list. Stamp icon
// refs in pass_design are unused by the event renderers but PassDesignSchema
// requires them — keep the loyalty defaults so one pass_design shape serves all
// three types.
export function buildDefaultEventTicketDraft() {
    const loyalty = buildDefaultProgramDraft();
    return {
        name: 'Event',
        onboarding_fields: loyalty.onboarding_fields,
        pass_design: {
            ...loyalty.pass_design,
            points_label: 'Tier',
            account_name_label: 'Attendee',
        },
        back_links: [],
        owner_terms: '',
        type_config: {
            event_name: '',
            starts_at: '',
            ends_at: undefined,
            venue_name: '',
            venue_address: undefined,
            venue_lat: null,
            venue_lng: null,
            holder_name_required: false,
            capacity: null,
            tiers: [
                {
                    id: 'general',
                    name: DEFAULT_EVENT_TIER_NAME,
                    price_centavos: null,
                    inclusions: [],
                    capacity: null,
                },
            ],
        },
    };
}
// The generic per-type config union used by the shared program schemas. Event
// FIRST: its required `event_name` key makes a membership config fall through
// cleanly (membership never carries the key). A malformed event config that
// slips through to membership is caught by the event service branch re-parsing
// with EventTicketConfigSchema.
export const TypeConfigSchema = z.union([
    EventTicketConfigSchema,
    MembershipConfigSchema,
]);
// Shared cross-field rules for the milestone list — applied by both the draft
// patch schema and the strict activation schema. Returns a list of issue
// strings (empty = valid) so callers can either throw or surface them in UI.
export function milestoneRuleIssues(milestones, totalStamps) {
    const issues = [];
    if (milestones.length < 1) {
        issues.push('milestones: at least one milestone (the grand prize) is required');
        return issues;
    }
    const finals = milestones.filter((m) => m.type === 'final');
    if (finals.length !== 1) {
        issues.push('milestones: exactly one grand prize (final milestone) is required');
    }
    const positions = milestones.map((m) => m.at_stamp);
    if (new Set(positions).size !== positions.length) {
        issues.push('milestones: each milestone must be at a distinct stamp count');
    }
    if (positions.some((p) => p < 1 || p > totalStamps)) {
        issues.push(`milestones: stamp positions must be between 1 and ${totalStamps}`);
    }
    if (finals[0] && finals[0].at_stamp !== totalStamps) {
        issues.push('milestones: the grand prize must be at the final stamp');
    }
    return issues;
}
// The fields the builder can patch. Everything is optional — a PATCH carries
// only the changed keys. Structural-lock enforcement (which of these are
// frozen after activation) lives in the API service, not the schema.
export const ProgramDraftPatchSchema = z
    .object({
    name: z.string().min(1).max(60).optional(),
    total_stamps: z
        .number()
        .int()
        .min(LOYALTY_MIN_STAMPS)
        .max(LOYALTY_MAX_STAMPS)
        .optional(),
    completion_behavior: CompletionBehaviorSchema.optional(),
    milestones: z.array(MilestoneSchema).optional(),
    onboarding_fields: OnboardingFieldsSchema.optional(),
    pass_design: PassDesignSchema.optional(),
    back_links: BackLinksSchema.optional(),
    owner_terms: z.string().max(OWNER_TERMS_MAX_LENGTH).optional(),
    // Owner toggle (program list page) — cosmetic/live, editable on an active
    // program of either type (not structural). Gates the staff enroll picker.
    staff_enrollable: z.boolean().optional(),
    // Owner toggle (program list page), same shape as staff_enrollable — may
    // staff recover passes ("Recover pass") for this program's cards.
    staff_can_recover_pass: z.boolean().optional(),
    // SLICE-REFERRAL — cosmetic/live config, editable on an active program (no
    // STRUCTURAL_LOCKED). The service gates this on tenant.referral_entitled
    // and re-checks referralConfigIssues against the persisted total_stamps.
    referral_config: ReferralConfigSchema.optional(),
    // Secret-menu — cosmetic/live in the sense that it's editable on an
    // active program, but `required_secret_stamps`/`reward_text` LOCK after
    // their first real save (service-enforced, see SecretMenuConfigSchema's
    // doc comment). The service gates writes on tenant.secret_menu_entitled
    // and re-checks secretMenuConfigIssues against the persisted total_stamps.
    secret_menu_config: SecretMenuConfigSchema.optional(),
    // Expiration — all four move together (the builder always patches the
    // whole config). Optional as a group; the API re-validates the shape +
    // the stay_completed-only rule against the persisted row.
    expiration_mode: ExpirationModeSchema.optional(),
    expiration_relative_unit: ExpirationRelativeUnitSchema.nullable().optional(),
    expiration_relative_amount: z
        .number()
        .int()
        .positive()
        .nullable()
        .optional(),
    // '' (the builder's "fixed mode selected, no date picked yet" state) coerces
    // to null so a mid-edit autosave doesn't 400 — the shape is enforced at
    // activation, not on every draft patch (same philosophy as milestones).
    expiration_fixed_date: z
        .preprocess((v) => (v === '' ? null : v), z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a YYYY-MM-DD date')
        .nullable())
        .optional(),
    // SLICE-MEMBERSHIP-PASS / SLICE-EVENT-TICKET — per-type config. Only valid
    // on a membership or event program; the service rejects it on loyalty rows
    // (and rejects loyalty structural fields on the typed branches). The event
    // branch re-parses with EventTicketConfigSchema as a safety net.
    type_config: TypeConfigSchema.optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'At least one field must be provided')
    .superRefine((v, ctx) => {
    // If the patch touches both milestones and total_stamps we can check the
    // cross-field rules eagerly. If it touches only one, the service re-checks
    // against the persisted row before activation — a draft is allowed to be
    // transiently inconsistent mid-edit.
    if (v.milestones !== undefined && v.total_stamps !== undefined) {
        for (const issue of milestoneRuleIssues(v.milestones, v.total_stamps)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue });
        }
    }
});
// Strict whole-program schema — what a program must satisfy to go active.
// The activation route validates the persisted row against this, then runs
// collectProgramActivationGaps for the cross-field rules.
export const ProgramActivateSchema = z.object({
    name: z.string().min(1).max(60),
    total_stamps: z
        .number()
        .int()
        .min(LOYALTY_MIN_STAMPS)
        .max(LOYALTY_MAX_STAMPS),
    completion_behavior: CompletionBehaviorSchema,
    milestones: z.array(MilestoneSchema).min(1),
    onboarding_fields: OnboardingFieldsSchema,
    pass_design: PassDesignSchema,
    back_links: BackLinksSchema,
    owner_terms: z.string().max(OWNER_TERMS_MAX_LENGTH),
    expiration_mode: ExpirationModeSchema,
    expiration_relative_unit: ExpirationRelativeUnitSchema.nullable(),
    expiration_relative_amount: z.number().int().positive().nullable(),
    expiration_fixed_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a YYYY-MM-DD date')
        .nullable(),
});
// Cross-field activation gaps not expressible in the flat schema. Empty list
// means the program is activation-ready. Reused by the wizard's Review step.
export function collectProgramActivationGaps(p) {
    const gaps = milestoneRuleIssues(p.milestones, p.total_stamps);
    const grand = p.milestones.find((m) => m.type === 'final');
    if (grand && grand.reward_text.trim().length === 0) {
        gaps.push('milestones: the grand prize needs a reward description');
    }
    for (const m of p.milestones) {
        if (m.reward_text.trim().length === 0) {
            gaps.push(`milestones: the milestone at stamp ${m.at_stamp} needs a reward description`);
        }
    }
    // Expiration: shape must be internally consistent, and an expiry can only
    // be set on a 'stay_completed' program — a 'reset' card recycles forever.
    gaps.push(...expirationShapeIssues(p));
    if (p.expiration_mode !== 'none' && p.completion_behavior !== 'stay_completed') {
        gaps.push('expiration: card expiry is only available when the card is marked complete forever');
    }
    return gaps;
}
// Full program row as returned by GET /api/owner/program.
export const ProgramSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    name: z.string(),
    status: ProgramStatusSchema,
    // SLICE-MEMBERSHIP-PASS — discriminator + per-type config. Loyalty rows:
    // pass_type 'loyalty', type_config null. Membership rows: structural loyalty
    // fields are null in the DB; the API serializer carries them as null.
    pass_type: z.enum(['loyalty', 'membership', 'event_ticket']).default('loyalty'),
    type_config: TypeConfigSchema.nullable().default(null),
    // Owner-controlled — may staff enroll customers into this program? Loyalty
    // defaults true, membership false (the gated owner-only hand-off).
    staff_enrollable: z.boolean().default(true),
    // Owner-controlled — may staff recover passes for this program's cards.
    staff_can_recover_pass: z.boolean().default(false),
    total_stamps: z.number().int().nullable(),
    completion_behavior: CompletionBehaviorSchema.nullable(),
    milestones: z.array(MilestoneSchema).nullable(),
    onboarding_fields: OnboardingFieldsSchema,
    pass_design: PassDesignSchema,
    back_links: BackLinksSchema,
    owner_terms: z.string().nullable(),
    expiration_mode: ExpirationModeSchema,
    expiration_relative_unit: ExpirationRelativeUnitSchema.nullable(),
    expiration_relative_amount: z.number().int().nullable(),
    expiration_fixed_date: z.string().nullable(),
    // SLICE-REFERRAL — per-program referral config (cosmetic/live JSONB). null
    // when unconfigured. Editable on an active program via the owner Referrals
    // page (the program PATCH gates it on tenant entitlement).
    referral_config: ReferralConfigSchema.nullable(),
    // Secret-menu config (cosmetic/live JSONB, null when unconfigured).
    // required_secret_stamps/reward_text lock after their first real save.
    secret_menu_config: SecretMenuConfigSchema.nullable(),
    activated_at: z.string().datetime().nullable(),
    activated_by: z.string().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
// Sensible defaults for a freshly-created draft. The builder opens on these;
// the API's createDraftProgram inserts them. Grand prize starts blank — the
// owner fills the reward text in wizard step 2.
export function buildDefaultProgramDraft() {
    return {
        name: 'Rewards',
        total_stamps: LOYALTY_DEFAULT_STAMPS,
        completion_behavior: 'reset',
        expiration_mode: 'none',
        expiration_relative_unit: null,
        expiration_relative_amount: null,
        expiration_fixed_date: null,
        milestones: [
            {
                at_stamp: LOYALTY_DEFAULT_STAMPS,
                reward_text: '',
                type: 'final',
            },
        ],
        onboarding_fields: {
            first_name: { enabled: true, required: true },
            email: { enabled: true, required: true },
            last_name: { enabled: false, required: false },
            // Birthday defaults ON but NOT required — collected by default so the
            // birthday broadcast segment has data to target, without forcing the
            // customer to fill it at sign-up. The owner can still disable it in the
            // builder. Existing programs are unaffected (snapshot at enrollment).
            birthday: { enabled: true, required: false },
            phone: { enabled: false, required: false },
        },
        pass_design: {
            primary_color: NAYA_TERRACOTTA,
            text_color: '#FFFFFF',
            stamp_filled_url: `builtin:${DEFAULT_STAMP_ICON}:filled`,
            stamp_empty_url: `builtin:${DEFAULT_STAMP_ICON}:empty`,
            front_fields: [...DEFAULT_FRONT_FIELDS],
            points_label: 'Stamps',
            pass_description: '',
            account_name_label: 'Member',
            stamp_color: '#FFFFFF',
            hero_original_url: null,
            hero_brightness: 0,
            hero_wallet_image_url: null,
        },
        back_links: [],
        owner_terms: '',
    };
}
// ----- Requests -----
const UlidLike = z.string().min(1);
export const EnrollCustomerRequest = z.object({
    first_name: z.string().min(1).max(60).optional(),
    birth_month_day: z
        .string()
        .regex(/^\d{2}-\d{2}$/, 'Must be MM-DD')
        .optional(),
    consented_at: z.string().datetime(),
});
export const IssueStampRequest = z
    .object({
    branch_id: UlidLike,
    idempotency_key: UlidLike,
    // Multi-stamp selector — how many stamps to add in this one action.
    // Defaults to 1 (existing single-stamp callers are unchanged). The server
    // caps the actual count added at whatever room remains on the card; it
    // never overflows past total_stamps or auto-resets the card.
    count: z.number().int().min(1).max(LOYALTY_MAX_STAMPS).optional(),
    // Secret-menu — how many of the `count` stamps in THIS action were
    // off-menu orders (a single visit can mix regular + secret items).
    // Defaults to 0. Bounded against `count` so a request can never claim
    // more secret stamps than stamps requested. Server additionally forces
    // this to 0 unless the tenant/program secret-menu gate is active — never
    // trust the client's number blindly.
    secret_count: z.number().int().min(0).max(LOYALTY_MAX_STAMPS).optional(),
})
    .refine((v) => (v.secret_count ?? 0) <= (v.count ?? 1), {
    message: 'secret_count cannot exceed count',
    path: ['secret_count'],
});
export const RedeemMilestoneRequest = z.object({
    milestone_at_stamp: z.number().int().min(1).max(LOYALTY_MAX_STAMPS),
    branch_id: UlidLike,
    idempotency_key: UlidLike,
    // Secret-menu — staff's choice when the final milestone is eligible for
    // both rewards. Ignored (never trusted) unless the server independently
    // confirms eligibility from the card's actual banked secret-stamp count.
    use_secret_reward: z.boolean().optional(),
});
// ----- Customer lifecycle (2026-09-05) -----
// Owner-only stamp correction + revoke/erase. See CLAUDE.md-adjacent
// conversation with Gatchaa: correction is a signed delta + required reason
// (not a raw override) so the ledger reads like "undo 2 accidental stamps",
// not a guessed absolute number.
const REASON_MAX_LENGTH = 300;
export const StampAdjustmentRequest = z
    .object({
    // Bounded to a single program's max card size in either direction — a
    // correction is never more than "the whole card was wrong".
    delta: z
        .number()
        .int()
        .refine((v) => Math.abs(v) <= LOYALTY_MAX_STAMPS, {
        message: `delta cannot exceed ${LOYALTY_MAX_STAMPS} in either direction`,
    }),
    // Independent correction to how many of the CURRENT cycle's stamps are
    // tagged secret-menu — only meaningful (and only accepted server-side)
    // when the tenant/program has secret-menu tagging enabled. Defaults to 0
    // (no change) so existing callers are unaffected.
    secret_delta: z
        .number()
        .int()
        .refine((v) => Math.abs(v) <= LOYALTY_MAX_STAMPS, {
        message: `secret_delta cannot exceed ${LOYALTY_MAX_STAMPS} in either direction`,
    })
        .optional()
        .default(0),
    reason: z.string().min(1).max(REASON_MAX_LENGTH),
})
    .refine((v) => v.delta !== 0 || v.secret_delta !== 0, {
    message: 'delta or secret_delta must be non-zero',
    path: ['delta'],
});
export const RevokeCustomerRequest = z.object({
    reason: z.string().min(1).max(REASON_MAX_LENGTH),
});
export const EraseCustomerRequest = z.object({
    // How the erasure request came in (e.g. "in person", "messenger") — a
    // compliance note, never the erased PII itself.
    requested_via: z.string().min(1).max(REASON_MAX_LENGTH),
});
// ----- Tenant / branch -----
/**
 * Canonical tenant slug check. Rejects bad format and reserved values. The
 * caller distinguishes 422 SLUG_INVALID_FORMAT vs 422 SLUG_RESERVED via the
 * named issue path; SlugSchema itself just returns invalid.
 */
export const SlugSchema = z
    .string()
    .superRefine((value, ctx) => {
    if (!validateSlugFormat(value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['format'], message: 'invalid format' });
        return;
    }
    if (isReservedSlug(value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reserved'], message: 'reserved' });
    }
});
export const TenantSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(120),
    slug: z.string(),
    plan_tier: z.enum(PLANS),
    billing_cycle: z.enum(BILLING_CYCLES),
    status: z.enum(TENANT_STATUSES),
    notes: z.string().nullable(),
    current_period_start: z.string().datetime().nullable(),
    current_period_end: z.string().datetime().nullable(),
    paid_through: z.string().datetime().nullable(),
    created_at: z.string().datetime(),
});
export const BranchSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    name: z.string().min(1).max(120),
    created_at: z.string().datetime(),
});
// ----- User -----
export const UserSchema = z.object({
    id: z.string(),
    tenant_id: z.string().nullable(), // null for superadmin
    branch_id: z.string().nullable(), // null for owner/superadmin
    role: z.enum(ROLES),
    email: z.string().email().nullable(), // null for staff (username-only)
    username: z.string().nullable(), // null for owner/superadmin (email-only)
    display_name: z.string().min(1).max(120),
    created_at: z.string().datetime(),
});
// ----- Customer (end user with a wallet pass) -----
export const CustomerSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    first_name: z.string().min(1).max(60).nullable(),
    birth_month_day: z
        .string()
        .regex(/^\d{2}-\d{2}$/, 'Must be MM-DD')
        .nullable(),
    consented_at: z.string().datetime(),
    created_at: z.string().datetime(),
});
// ----- Broadcast -----
export const BroadcastSegmentSchema = z.enum(['everyone', 'birthday_this_month']);
export const BROADCAST_VALIDITY_TEXT_MAX = 80;
/**
 * Slice 5b — optional "validity" line appended to the broadcast body at send
 * time. Three discriminated shapes:
 *   - `text`  : free-form "Valid: while supplies last"
 *   - `date`  : single end date(time), "Valid until May 20"
 *   - `range` : start + end date(time), "Valid May 19 – 20"
 *
 * `includes_time` decides whether the formatter renders the clock part. When
 * false, date-only end values are interpreted as end-of-day Asia/Manila (the
 * composer does that math before sending — server stores the exact
 * timestamptz).
 */
export const BroadcastValiditySchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('text'),
        text: z.string().trim().min(1).max(BROADCAST_VALIDITY_TEXT_MAX),
    }),
    z.object({
        kind: z.literal('date'),
        end_at: z.string().datetime(),
        includes_time: z.boolean(),
    }),
    z
        .object({
        kind: z.literal('range'),
        start_at: z.string().datetime(),
        end_at: z.string().datetime(),
        includes_time: z.boolean(),
    })
        .refine((v) => new Date(v.start_at) < new Date(v.end_at), {
        message: 'Start must be before end',
        path: ['end_at'],
    }),
]);
// ----- Broadcast audience segmentation -----
//
// A broadcast's audience is one of four `kind`s. `everyone` and
// `birthday_this_month` are the slice-5 legacy segments, kept as first-class
// kinds so their send paths (class-level message / per-object birthday loop)
// are untouched. `preset` is a curated, named segment; `filter` is an
// owner-composed set of AND-combined rules. `preset` and `filter` both
// resolve, server-side, to the same per-card matching logic.
// A single filter rule. Discriminated on `dimension`; each shape carries only
// the operators/values that dimension supports. `value` is an integer
// (stamp count or day count depending on dimension).
export const SegmentRuleSchema = z.discriminatedUnion('dimension', [
    z.object({
        dimension: z.literal('stamps'),
        op: z.enum(['eq', 'gte', 'lte', 'between']),
        value: z.number().int().min(0).max(LOYALTY_MAX_STAMPS),
        // `value2` is the upper bound, required only for `between`.
        value2: z.number().int().min(0).max(LOYALTY_MAX_STAMPS).optional(),
    }),
    z.object({
        dimension: z.literal('milestone_gap'),
        op: z.enum(['eq', 'lte']),
        value: z.number().int().min(1).max(LOYALTY_MAX_STAMPS),
    }),
    z.object({
        dimension: z.literal('last_stamp'),
        op: z.enum(['within_days', 'older_than_days']),
        value: z.number().int().min(1).max(365),
    }),
    z.object({
        dimension: z.literal('enrolled'),
        op: z.enum(['within_days', 'older_than_days']),
        value: z.number().int().min(1).max(365),
    }),
    z.object({
        dimension: z.literal('reward_state'),
        op: z.enum(['has_unclaimed', 'no_stamps', 'completed_before']),
    }),
    z.object({
        dimension: z.literal('birthday'),
        op: z.literal('this_month'),
    }),
]);
// A `between` stamps rule must carry a valid upper bound.
const SegmentRuleRefined = SegmentRuleSchema.refine((r) => r.dimension !== 'stamps' ||
    r.op !== 'between' ||
    (r.value2 !== undefined && r.value2 >= r.value), { message: 'A "between" rule needs an upper bound ≥ the lower bound' });
export const BroadcastAudienceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('everyone') }),
    z.object({ kind: z.literal('birthday_this_month') }),
    // SLICE-MEMBERSHIP-PASS — broadcast to one membership tier. Only valid for a
    // membership program with tiers enabled (the create route + audience resolver
    // scope it to that program's cards whose effective tier matches). Resolved
    // directly in resolveAudience, not via the SegmentRule machinery (stamps).
    z.object({
        kind: z.literal('membership_tier'),
        tier: z.string().min(1).max(MEMBERSHIP_TIER_MAX_LENGTH),
    }),
    z.object({
        kind: z.literal('preset'),
        preset: z.enum(BROADCAST_PRESET_KEYS),
    }),
    z.object({
        kind: z.literal('filter'),
        // 'all' = every rule must match (AND); 'any' = at least one (OR). One
        // toggle for the whole rule set — no per-rule mixing (it would need
        // grouping to be unambiguous).
        match: z.enum(['all', 'any']),
        rules: z.array(SegmentRuleRefined).min(1).max(MAX_SEGMENT_RULES),
    }),
]);
// Canonical rule sets backing each curated preset. The resolver expands a
// `preset` audience into these rules — one source of truth, so a preset and
// the equivalent hand-built filter resolve identically. Labels/descriptions
// for the UI live in `BROADCAST_PRESET_META` (constants).
export const BROADCAST_PRESETS = {
    almost_at_reward: [{ dimension: 'milestone_gap', op: 'lte', value: 2 }],
    one_stamp_away: [{ dimension: 'milestone_gap', op: 'eq', value: 1 }],
    new_no_stamps: [{ dimension: 'reward_state', op: 'no_stamps' }],
    reward_waiting: [{ dimension: 'reward_state', op: 'has_unclaimed' }],
    lapsed_60d: [{ dimension: 'last_stamp', op: 'older_than_days', value: 60 }],
    active_30d: [{ dimension: 'last_stamp', op: 'within_days', value: 30 }],
    joined_this_week: [{ dimension: 'enrolled', op: 'within_days', value: 7 }],
    birthday_month: [{ dimension: 'birthday', op: 'this_month' }],
};
// Expand any audience to its canonical rule list. `everyone` → no rules
// (every reachable card). `birthday_this_month` → the birthday rule. `preset`
// → the preset's rules. `filter` → its own rules. The API resolver applies
// these against the card set; this keeps "what a preset means" in one place.
export function audienceRules(audience) {
    switch (audience.kind) {
        case 'everyone':
            return [];
        case 'membership_tier':
            // No stamp-rule expansion — resolveAudience filters by tier directly.
            return [];
        case 'birthday_this_month':
            return [{ dimension: 'birthday', op: 'this_month' }];
        case 'preset':
            return BROADCAST_PRESETS[audience.preset];
        case 'filter':
            return audience.rules;
    }
}
// The vocabulary of "enableable" broadcast segments for feature-gating. A
// plan tier / per-tenant override lists which of these an owner may use:
// the two legacy kinds plus every curated preset key. The custom *filter
// builder* is gated by a separate boolean — it's a capability, not a segment.
export const BROADCAST_SEGMENT_KEYS = [
    'everyone',
    'birthday_this_month',
    ...BROADCAST_PRESET_KEYS,
];
export const BroadcastSegmentKeySchema = z.enum(BROADCAST_SEGMENT_KEYS);
// A short human label for an audience — used in history cards / audit text.
export function audienceLabel(audience) {
    switch (audience.kind) {
        case 'everyone':
            return 'Everyone';
        case 'membership_tier':
            return `${audience.tier} tier`;
        case 'birthday_this_month':
            return 'Birthday this month';
        case 'preset':
            return BROADCAST_PRESET_META[audience.preset].label;
        case 'filter':
            return audience.rules.length === 1
                ? 'Custom filter'
                : `Custom filter (${audience.rules.length} rules)`;
    }
}
// One filter rule as a plain-English clause, phrased to follow "customers
// who …" — e.g. "have between 5 and 7 stamps", "joined in the last 7 days".
function ruleToEnglish(rule) {
    switch (rule.dimension) {
        case 'stamps':
            switch (rule.op) {
                case 'eq':
                    return `have exactly ${rule.value} stamps`;
                case 'gte':
                    return `have ${rule.value} or more stamps`;
                case 'lte':
                    return `have ${rule.value} or fewer stamps`;
                case 'between':
                    return `have between ${rule.value} and ${rule.value2 ?? rule.value} stamps`;
            }
            return 'match a stamp-count filter';
        case 'milestone_gap': {
            const s = rule.value === 1 ? 'stamp' : 'stamps';
            return rule.op === 'eq'
                ? `are exactly ${rule.value} ${s} from a reward`
                : `are within ${rule.value} ${s} of a reward`;
        }
        case 'last_stamp':
            return rule.op === 'within_days'
                ? `earned a stamp in the last ${rule.value} days`
                : `haven't earned a stamp in over ${rule.value} days`;
        case 'enrolled':
            return rule.op === 'within_days'
                ? `joined in the last ${rule.value} days`
                : `joined more than ${rule.value} days ago`;
        case 'reward_state':
            switch (rule.op) {
                case 'has_unclaimed':
                    return 'have an unclaimed reward';
                case 'no_stamps':
                    return 'have no stamps yet';
                case 'completed_before':
                    return 'have completed a card before';
            }
            return 'match a reward-status filter';
        case 'birthday':
            return 'have a birthday this month';
    }
}
// Join clauses into a natural list — "A", "A and B", "A, B, and C".
function joinClauses(clauses, joiner) {
    if (clauses.length <= 1)
        return clauses[0] ?? '';
    if (clauses.length === 2)
        return `${clauses[0]} ${joiner} ${clauses[1]}`;
    return `${clauses.slice(0, -1).join(', ')}, ${joiner} ${clauses[clauses.length - 1]}`;
}
/**
 * A plain-English description of who an audience reaches — shown in the
 * composer so a non-technical owner can read back exactly what their custom
 * filter means without parsing the rule rows.
 */
export function audienceSummary(audience) {
    switch (audience.kind) {
        case 'everyone':
            return 'Reaches every customer who has your pass.';
        case 'membership_tier':
            return `Reaches members on the ${audience.tier} tier.`;
        case 'birthday_this_month':
            return 'Reaches customers whose birthday is this month.';
        case 'preset':
            // The preset's own description already reads as an audience sentence.
            return BROADCAST_PRESET_META[audience.preset].description;
        case 'filter': {
            const clauses = audience.rules.map(ruleToEnglish);
            const joiner = audience.match === 'any' ? 'or' : 'and';
            return `Reaches customers who ${joinClauses(clauses, joiner)}.`;
        }
    }
}
export const BroadcastSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    title: z.string().min(1).max(50),
    body: z.string().min(1).max(500),
    link_url: z.string().url().nullable(),
    segment: BroadcastSegmentSchema,
    audience: BroadcastAudienceSchema.nullable(),
    scheduled_for: z.string().datetime().nullable(),
    sent_at: z.string().datetime().nullable(),
    created_at: z.string().datetime(),
});
// Owner-supplied input on POST /api/broadcasts. Server fills the rest.
// `audience` carries the segmentation choice; the legacy `force_send` field
// is gone — the soft 7-day nudge is now advisory-only and never blocks.
export const BroadcastInputSchema = z.object({
    title: z.string().min(1).max(50),
    body: z.string().min(1).max(500),
    link_url: z.string().url().startsWith('https://').optional(),
    audience: BroadcastAudienceSchema,
    // SLICE-PASS-TYPES — which program to broadcast to. Optional: omitted means
    // the tenant's default program (single-program tenants never send it). Scopes
    // both the audience and the 'everyone' class-level message to that program.
    program_id: z.string().optional(),
    scheduled_for: z.string().datetime().optional(),
    validity: BroadcastValiditySchema.optional(),
});
// Body of POST /api/broadcasts/preview-count — the audience plus the program it
// will be scoped to, so the previewed recipient count matches what's delivered.
export const BroadcastPreviewSchema = z.object({
    audience: BroadcastAudienceSchema,
    program_id: z.string().optional(),
});
// Tenant branding — owner-editable visual fields used in the wallet class.
// Color is sourced from loyalty_programs.pass_design.primary_color (edited in
// the program builder), NOT from this payload — tenant.brand_color is dropped.
export const WalletClassInputSchema = z.object({
    logo_url: z
        .string()
        .url()
        .startsWith('https://')
        .optional(),
    program_name: z.string().min(1).max(60).optional(),
});
// Public enrollment payload. All 5 onboarding fields are optional at the schema
// level — per-program "required" enforcement is layered on top by
// `validateAgainstProgramRequiredFields` (which reads `program.onboarding_fields`
// at the route boundary). Consent must be the literal `true` — anything else
// (including missing) is rejected with CONSENT_REQUIRED.
//
// API field-name vs DB-column note: `birthday` is the API key (matches
// `onboarding_fields.birthday` from 4a-3 builder); the DB column stays
// `customers.birth_month_day` (MM-DD). The route maps API → column at the
// service boundary.
export const PublicEnrollRequestSchema = z.object({
    first_name: z.string().min(1).max(60).optional(),
    last_name: z.string().min(1).max(60).optional(),
    // Normalised to lower-case so it round-trips with the exact-match customer
    // search (owner/staff stamp lookup): emails are case-insensitive in
    // practice, but a verbatim-stored `Foo@x.com` would never match a
    // `lower()`-ed search query.
    email: z
        .string()
        .email()
        .max(254)
        .transform((s) => s.toLowerCase())
        .optional(),
    birthday: z
        .string()
        .regex(/^\d{2}-\d{2}$/, 'Must be MM-DD')
        .optional(),
    phone: z.string().min(1).max(20).optional(),
    consent: z.literal(true),
    // SLICE-MEMBERSHIP-PASS — the member-chosen tier, only meaningful for a
    // self-enroll membership whose program has tiers on. Validated against the
    // program's tier list at the route (resolveEnrollmentTier); ignored for
    // loyalty + for gated memberships (those carry the staff-chosen tier on the
    // enrollment token instead).
    tier_name: z.string().min(1).max(MEMBERSHIP_TIER_MAX_LENGTH).optional(),
    // SLICE-REFERRAL — optional referral code (from the /r/<code> Join button or
    // the manual-entry accordion). Normalised to the on-card uppercase form; an
    // unknown/inactive code just resolves to no referral, never an error.
    referral_code: z
        .string()
        .min(1)
        .max(40)
        .transform((s) => s.trim().toUpperCase())
        .optional(),
    // Honeypot. A hidden form field a real human never fills. Bots that blindly
    // complete every input submit a non-empty value; the route then silently
    // succeeds without enrolling. Optional + capped so a missing field (older
    // cached PWA bundles) or an oversized value never breaks a real enrollment.
    _hp: z.string().max(200).optional(),
    // Same-phone pass recovery key this browser kept from an earlier enrollment.
    // Only consulted when the email is already enrolled: a key for THAT card
    // re-issues its save link (see apps/api/src/services/pass-recovery-key.ts).
    recovery_key: z.string().max(200).optional(),
});
// Same-phone pass recovery — the signed key the enrolling browser kept in
// localStorage (see apps/api/src/services/pass-recovery-key.ts).
export const PublicPassRecoveryRequestSchema = z.object({
    key: z.string().min(1).max(200),
});
/**
 * Cross-checks a parsed enrollment payload against the program's onboarding
 * field config: every onboarding field marked `required: true` AND `enabled:
 * true` must be present (non-empty) in the payload.
 *
 * Returns the list of missing required field keys; an empty array means the
 * payload satisfies the program's required-field set. Field-key labels for
 * error messages come from {@link ONBOARDING_FIELD_LABELS}.
 *
 * Behavioural notes:
 * - The schema-level `first_name` / `email` are immutable + always enabled
 *   from the program's POV, but they're only *required* if the owner toggled
 *   it; this helper is the single source of truth for that distinction.
 * - `birthday` is checked under the API key, not `birth_month_day`. The route
 *   handler is responsible for the API → column mapping.
 */
export function validateAgainstProgramRequiredFields(payload, onboarding) {
    const missing = [];
    const isMissing = (v) => v === undefined || v === null || v === '';
    for (const key of ALL_ONBOARDING_FIELDS) {
        const cfg = onboarding[key];
        if (!cfg.enabled || !cfg.required)
            continue;
        if (isMissing(payload[key]))
            missing.push(key);
    }
    return missing;
}
// ----- Wallet passes (provider-agnostic) -----
export const WalletProviderEnum = z.enum(WALLET_PROVIDERS);
// Outward-facing pass summary — `external_id` is intentionally omitted from
// API responses (internal provider identifier).
export const WalletPassSummarySchema = z.object({
    provider: WalletProviderEnum,
    status: z.enum(['pending', 'active', 'failed']),
    last_synced_at: z.string().datetime().nullable(),
});
export const SaveTokenResponseSchema = z.object({
    token: z.string(),
    save_url: z.string().url(),
    expires_at: z.string().datetime(),
});
export const CreateCardResponseSchema = z.object({
    card: z.unknown(),
    save_url: z.string().url(),
    providers_available: z.array(WalletProviderEnum).min(1),
});
// ----- Superadmin (slice 3a) -----
export const AuditActionSchema = z.enum(AUDIT_ACTIONS);
export const InvoiceStatusSchema = z.enum(INVOICE_STATUSES);
export const AdminLoginRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
// Slice 6 — admin tenant create is intentionally minimal. The admin sets
// plan tier, billing cycle, seat caps, and notes. The owner provides their
// own email, name, business name, and URL slug during the setup wizard so
// the admin doesn't act as a typing intermediary. Backend inserts a
// placeholder owner row + temp slug at create; both get patched in-wizard.
export const AdminCreateTenantRequestSchema = z.object({
    plan_tier: z.enum(PLANS),
    billing_cycle: z.enum(BILLING_CYCLES),
    notes: z.string().max(2000).optional(),
    // Hard caps enforced by owner-side create endpoints. Defaults applied
    // server-side when omitted.
    max_staff: z.number().int().min(0).max(100).optional(),
    max_branches: z.number().int().min(1).max(100).optional(),
    // Broadcast cadence. Either field can be null = unlimited; the worker
    // applies whichever cap (per_day or per_week) is reached first. Default
    // per_day = unlimited (null), per_week = 1 (preserves existing V1
    // behavior — admin can lift per-tenant).
    max_broadcasts_per_day: z.number().int().min(0).max(100).nullable().optional(),
    max_broadcasts_per_week: z
        .number()
        .int()
        .min(0)
        .max(1000)
        .nullable()
        .optional(),
    // Per-tenant broadcast-capability overrides. Null/omitted = inherit the
    // plan tier. `broadcast_presets_enabled` overrides the allowed segment
    // list; `broadcast_filter_builder_enabled` overrides the builder flag.
    broadcast_presets_enabled: z
        .array(BroadcastSegmentKeySchema)
        .nullable()
        .optional(),
    broadcast_filter_builder_enabled: z.boolean().nullable().optional(),
    // SLICE-PASS-TYPES — per-type program caps at create. null/omitted = inherit
    // the plan tier default; a map = explicit per-tenant override.
    program_caps: ProgramCapsSchema.nullable().optional(),
    // SLICE-REFERRAL — superadmin entitlement. Omitted = default off.
    referral_entitled: z.boolean().optional(),
    // Secret-menu superadmin entitlement. Omitted = default off.
    secret_menu_entitled: z.boolean().optional(),
});
export const AdminPatchTenantRequestSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    notes: z.string().nullable().optional(),
    billing_cycle: z.enum(BILLING_CYCLES).optional(),
    // Slice 4b — admin can adjust the staff seat cap any time. Lowering it
    // below the current staff count is allowed (existing staff stay, but
    // no new seats can be added until the count drops).
    max_staff: z.number().int().min(0).max(100).optional(),
    // Slice 6 — admin can raise/lower the branch cap any time. Min 1 (a
    // tenant always has at least the first branch they create in setup).
    max_branches: z.number().int().min(1).max(100).optional(),
    // Slice 6 — broadcast cadence. Null = no cap on that window.
    max_broadcasts_per_day: z
        .number()
        .int()
        .min(0)
        .max(100)
        .nullable()
        .optional(),
    max_broadcasts_per_week: z
        .number()
        .int()
        .min(0)
        .max(1000)
        .nullable()
        .optional(),
    // Per-tenant broadcast-capability overrides. Null = clear the override
    // (back to plan-tier inheritance); an array/boolean sets it.
    broadcast_presets_enabled: z
        .array(BroadcastSegmentKeySchema)
        .nullable()
        .optional(),
    broadcast_filter_builder_enabled: z.boolean().nullable().optional(),
    // SLICE-PASS-TYPES — per-type program caps override. null clears the
    // override (inherit the plan tier); an object sets the explicit per-type map.
    program_caps: ProgramCapsSchema.nullable().optional(),
    // SLICE-REFERRAL — superadmin entitlement toggle. Editable any time.
    referral_entitled: z.boolean().optional(),
    // Secret-menu superadmin entitlement toggle. Editable any time.
    secret_menu_entitled: z.boolean().optional(),
    // Logo is NOT editable from admin: owners manage their own logo via the
    // crop UI on /branding (slice 4a-2), and an admin who needs to change it
    // impersonates the owner. One upload path, one source of truth. Slug is
    // also intentionally absent — immutable post-creation (slice 3c §1). And
    // plan_tier goes through PATCH /tenants/:id/plan-tier (pending fields,
    // effective next billing period), not here.
})
    .refine((v) => v.name !== undefined ||
    v.notes !== undefined ||
    v.billing_cycle !== undefined ||
    v.max_staff !== undefined ||
    v.max_branches !== undefined ||
    v.max_broadcasts_per_day !== undefined ||
    v.max_broadcasts_per_week !== undefined ||
    v.broadcast_presets_enabled !== undefined ||
    v.broadcast_filter_builder_enabled !== undefined ||
    v.program_caps !== undefined ||
    v.referral_entitled !== undefined ||
    v.secret_menu_entitled !== undefined, 'At least one field must be provided');
export const AdminSuspendTenantRequestSchema = z.object({
    reason: z.string().min(1).max(500),
});
export const AdminImpersonateRequestSchema = z.object({
    user_id: z.string().min(1),
});
export const AdminCreateInvoiceRequestSchema = z.object({
    period_start: z.string().datetime(),
    period_end: z.string().datetime(),
    plan_tier_at_issue: z.enum(PLANS).optional(),
    billing_cycle: z.enum(BILLING_CYCLES).optional(),
    amount_centavos: z.number().int().positive().optional(),
});
export const AdminMarkInvoicePaidRequestSchema = z.object({
    payment_method: z.enum(PAYMENT_METHODS),
    payment_reference: z.string().min(1).max(120).optional(),
    paid_at: z.string().datetime().optional(),
});
export const AdminVoidInvoiceRequestSchema = z.object({
    reason: z.string().min(1).max(500),
});
export const AdminPatchPlanTierRequestSchema = z
    .object({
    display_name: z.string().min(1).max(60).optional(),
    max_branches: z.number().int().positive().nullable().optional(),
    max_staff_per_branch: z.number().int().positive().nullable().optional(),
    max_active_customers: z.number().int().positive().nullable().optional(),
    broadcasts_enabled: z.boolean().optional(),
    // The curated segments (legacy kinds + preset keys) this plan tier
    // unlocks. The custom filter builder is gated separately below.
    broadcast_segments_enabled: z.array(BroadcastSegmentKeySchema).optional(),
    // Whether this plan tier unlocks the composable filter builder. Presets
    // are the baseline; the builder is the upsell.
    broadcast_filter_builder_enabled: z.boolean().optional(),
    // New-tenant default broadcast cadence caps for this tier. null =
    // unlimited on that window; a tenant created on this tier pre-fills its
    // own caps from these.
    max_broadcasts_per_day: z.number().int().min(0).max(100).nullable().optional(),
    max_broadcasts_per_week: z
        .number()
        .int()
        .min(0)
        .max(1000)
        .nullable()
        .optional(),
    // SLICE-PASS-TYPES — per-type program caps (tier DEFAULT). Full map; a
    // tenant inherits this when its own program_caps is null.
    program_caps: ProgramCapsSchema.optional(),
    monthly_price_centavos: z.number().int().positive().optional(),
    annual_price_centavos: z.number().int().positive().optional(),
    support_tier: z.enum(['email', 'email_telegram', 'priority']).optional(),
})
    .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided');
// ----- Superadmin (slice 3c) -----
// Branch CRUD under a tenant (admin-operated).
export const AdminCreateBranchRequestSchema = z.object({
    name: z.string().min(1).max(120),
});
// Slice 4b — lat/lng are optional + nullable; all-or-nothing enforced at the
// DB level by the branches_coords_both_or_neither_chk constraint, but we also
// guard here so the API returns a friendly 422 instead of a Postgres 23514.
export const AdminPatchBranchRequestSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    latitude: z.number().min(-90).max(90).nullable().optional(),
    longitude: z.number().min(-180).max(180).nullable().optional(),
})
    .refine((v) => v.name !== undefined ||
    v.latitude !== undefined ||
    v.longitude !== undefined, 'At least one field must be provided')
    .refine(
// Both-or-neither: when one coord is provided (including null), the other
// must be present with the matching null/non-null shape. Half-set pairs
// are a UI bug, not a partial save.
(v) => {
    if (v.latitude === undefined && v.longitude === undefined)
        return true;
    if (v.latitude === undefined || v.longitude === undefined)
        return false;
    return (v.latitude === null) === (v.longitude === null);
}, 'latitude and longitude must be provided together (both set or both null)');
export const AdminArchiveBranchRequestSchema = z.object({
    reason: z.string().min(1).max(500).optional(),
});
// Staff CRUD under a tenant (admin-operated). Staff identifier is email
// since migration 0013 — display_name is the human label, password is
// owner-relayed, branch_id assigns counter scope.
export const AdminCreateStaffRequestSchema = z.object({
    email: z.string().email(),
    display_name: z.string().min(1).max(120),
    password: z.string().min(12),
    branch_id: z.string().min(1),
});
export const AdminPatchStaffRequestSchema = z
    .object({
    display_name: z.string().min(1).max(120).optional(),
    branch_id: z.string().min(1).optional(),
})
    .refine((v) => v.display_name !== undefined || v.branch_id !== undefined, 'At least one field must be provided');
// Plan tier reassignment — effective next billing period (slice 3c).
export const AdminReassignPlanTierRequestSchema = z.object({
    plan_tier: z.enum(PLANS),
});
// Admin CRUD.
export const AdminCreateAdminRequestSchema = z.object({
    email: z.string().email(),
    name: z.string().min(1).max(120),
    temporary_password: z.string().min(12),
});
export const AdminChangePasswordRequestSchema = z
    .object({
    current_password: z.string().min(1),
    new_password: z.string().min(12),
})
    .refine((v) => v.current_password !== v.new_password, 'New password must differ from current password');
// Plan limit warnings — surfaced by /api/admin/tenants/:id and /api/tenant/limits.
export const PlanLimitWarningSchema = z.object({
    dimension: z.enum(['branches', 'staff_per_branch', 'active_customers']),
    current: z.number().int(),
    limit: z.number().int().nullable(),
    branch_id: z.string().nullable().optional(),
});
// ----- Owner / staff auth + branding (slice 4a-1) -----
// Owner password rule: minimum 10 characters, no other complexity rules
// (SLICE-4a-1 "Password requirements"). Used by both login (loose — just
// non-empty for the lookup) and the setup-consume / future reset flows.
export const OwnerPasswordSchema = z
    .string()
    .min(10, 'Password must be at least 10 characters');
// Owner login — email is the globally-unique identifier; no tenant slug.
export const OwnerLoginRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
// Setup-token consume — sets the owner's password and establishes a session.
// Slice 6 — optional email + display_name are accepted and applied in the
// same transaction so the wizard can collect "your details" BEFORE the
// password step (the owner sees profile → password but the network only
// makes one mutating call).
export const OwnerSetupConsumeRequestSchema = z.object({
    token: z.string().min(1),
    password: OwnerPasswordSchema,
    email: z.string().email().optional(),
    display_name: z.string().min(1).max(120).optional(),
});
// Setup-wizard step-1 email availability check. Token-gated so it isn't an open
// email-enumeration endpoint. Lets the wizard flag a taken email at the "your
// details" step instead of only at the password/consume step.
export const OwnerSetupCheckEmailRequestSchema = z.object({
    token: z.string().min(1),
    email: z.string().email(),
});
// Password-reset consume — sets a new password and opens a session for an
// existing (already-onboarded) owner. Same shape as setup consume; the
// distinction is that this route does not touch tenant status or wizard
// state, so it is safe for active/suspended tenants.
export const OwnerPasswordResetConsumeRequestSchema = z.object({
    token: z.string().min(1),
    password: OwnerPasswordSchema,
});
// Slice 6 — owner identity collected during the setup wizard. The admin
// creates the tenant with placeholder owner + slug; these two endpoints let
// the owner write their real values once.
export const OwnerSetupProfileRequestSchema = z.object({
    email: z.string().email(),
    display_name: z.string().min(1).max(120),
});
// Slug rules mirror the tenant slug check constraint exactly.
export const OwnerSetupSlugRequestSchema = z.object({
    slug: z
        .string()
        .min(3)
        .max(30)
        .regex(/^[a-z][a-z0-9-]{2,29}$/),
});
// Branding — business name patch. Logo upload is multipart, not JSON, so it
// has no body schema here.
export const OwnerPatchBrandingRequestSchema = z.object({
    business_name: z.string().min(1).max(120),
});
// First-branch creation from the setup wizard. Address is accepted but the
// branches table has no address column in V1 — the route ignores it for now
// (kept in the schema so the wizard form contract is stable). Name required.
export const OwnerCreateBranchRequestSchema = z.object({
    name: z.string().min(1).max(120),
    address: z.string().max(500).optional(),
});
// Slice 4b — owner-side branch edit. Name + lat/lng all optional + nullable
// (lat/lng only). Same both-or-neither shape as the admin patch.
export const OwnerPatchBranchRequestSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    latitude: z.number().min(-90).max(90).nullable().optional(),
    longitude: z.number().min(-180).max(180).nullable().optional(),
})
    .refine((v) => v.name !== undefined ||
    v.latitude !== undefined ||
    v.longitude !== undefined, 'At least one field must be provided')
    .refine((v) => {
    if (v.latitude === undefined && v.longitude === undefined)
        return true;
    if (v.latitude === undefined || v.longitude === undefined)
        return false;
    return (v.latitude === null) === (v.longitude === null);
}, 'latitude and longitude must be provided together (both set or both null)');
// Slice 4b iteration — owner staff CRUD. Mirror of AdminCreateStaffRequest
// minus the fields owners shouldn't set (no must_change_password override).
export const OwnerCreateStaffRequestSchema = z.object({
    email: z.string().email(),
    display_name: z.string().min(1).max(120),
    password: z.string().min(12),
    branch_id: z.string().min(1),
});
// Owner-initiated staff password reset. `password` is optional: when the
// owner types one in the UI it's used verbatim; when omitted the server
// generates a temporary password (the legacy behavior). Either way the new
// password is returned once for the owner to relay.
export const OwnerResetStaffPasswordRequestSchema = z.object({
    password: z.string().min(12).optional(),
});
// Staff login — tenant slug resolves the tenant, then (email, password)
// authenticate within that scope. Staff identifier was originally username;
// migration 0013 (and slice 4a-4c.2 follow-up) moves staff to email-only.
export const StaffLoginRequestSchema = z.object({
    slug: z.string().min(1),
    email: z.string().email(),
    password: z.string().min(1),
});
// Logo upload constraints — shared so the owner UI can validate client-side
// before the round-trip and the API can enforce server-side.
export const LOGO_MAX_BYTES = 5 * 1024 * 1024; // 5MB
export const LOGO_MIN_DIMENSION = 300;
export const LOGO_MAX_DIMENSION = 1500;
export const LOGO_ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg'];
// Hero photo upload constraints. The client crops to exactly 1032×336 (Google
// Wallet's heroImage spec) before posting, so the server enforces the same
// dimensions to keep the contract tight. Brightness is bounded to [-70, 0] —
// negative-only because the use case is "darken the photo so my stamps read."
export const HERO_WIDTH = 1032;
export const HERO_HEIGHT = 336;
export const HERO_MAX_BYTES = 5 * 1024 * 1024; // 5MB
export const HERO_BRIGHTNESS_MIN = -70;
export const HERO_BRIGHTNESS_MAX = 0;
export const HERO_ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg'];
//# sourceMappingURL=index.js.map